import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TrainingTab from "@/components/TrainingTab";
import SignaturePad from "@/components/SignaturePad";
import {
  ArrowLeft, Mail, BadgeCheck, Calendar, MapPin, UserCircle2, Building2,
  GraduationCap, FolderOpen, Archive, Camera, Trash2, ShieldCheck, PenLine,
  Upload, AlertTriangle,
} from "lucide-react";

const initialsOf = (name) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

// Deterministic warm earth-tone palette based on the name so each member gets
// a consistent fallback color before they upload a photo.
const AVATAR_PALETTE = ["#7B4A2D", "#A0623A", "#5C3A22", "#8B5A3C", "#6E4327", "#A57044", "#4F3220"];
const colorFor = (key) => {
  if (!key) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

export default function MemberProfile({ selfService = false }) {
  const { user: me, isModuleHidden } = useAuth();
  const params = useParams();
  const navigate = useNavigate();

  const targetId = selfService ? me?.id : params.id;
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [sites, setSites] = useState([]);
  const [fsmsAssignments, setFsmsAssignments] = useState([]);
  const [tab, setTab] = useState("profile");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!targetId || tab !== "fsms") return;
    (async () => {
      try {
        const ep = selfService ? "/fsms-team/me" : `/fsms-team/user/${targetId}`;
        const { data: d } = await api.get(ep);
        setFsmsAssignments(d.assignments || []);
      } catch (e) {
        console.error("FSMS assignments load failed:", e);
      }
    })();
  }, [targetId, tab, selfService]);

  useEffect(() => {
    if (!targetId) return;
    (async () => {
      try {
        // Fetch full profile (incl. email) for the target member, alongside the
        // tenant directory (for superior + direct-reports lookups) and sites.
        const [profile, dir, s] = await Promise.all([
          api.get(`/tenant/users/${targetId}`),
          api.get("/tenant/directory"),
          api.get("/sites"),
        ]);
        const all = dir.data.users || [];
        setTeam(all);
        setSites(s.data.sites || []);
        setData(profile.data.user);
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 403) setErr("You don't have permission to view this profile.");
        else if (e.response?.status === 404) setErr("Member not found.");
        else setErr(formatErr(detail) || "Failed to load profile");
      }
    })();
  }, [targetId]);

  const superior = useMemo(
    () => (data?.direct_superior_id ? team.find((u) => u.id === data.direct_superior_id) : null),
    [data, team]
  );
  const reports = useMemo(
    () => team.filter((u) => u.direct_superior_id === targetId && !u.is_archived),
    [team, targetId]
  );
  const siteNames = useMemo(
    () => (data?.site_ids || []).map((id) => sites.find((s) => s.id === id)?.name).filter(Boolean),
    [data, sites]
  );

  const isAdmin = me?.role === "owner" || me?.role === "manager";
  const isSelf = me?.id === targetId;
  const canEditAvatar = !data?.is_archived && (isSelf || isAdmin);
  // Signatures are personally owned; only the user themselves manages their
  // own signature, and only roles that ever sign reports (owner / manager /
  // supervisor) get the section at all.
  const SIGNATURE_ROLES = ["owner", "manager", "supervisor"];
  const canManageSignature = isSelf && !data?.is_archived && SIGNATURE_ROLES.includes(data?.role);
  const fileRef = useRef(null);
  const sigFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);
  // Toggles the in-page draw-pad. Stays in this component (no modal) so
  // it works the same on desktop and mobile.
  const [showPad, setShowPad] = useState(false);

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\//.test(f.type)) { setErr("Please pick an image (PNG / JPG / SVG)."); return; }
    if (f.size > 4 * 1024 * 1024) { setErr("Image too large (max 4 MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: res } = await api.post(`/tenant/users/${targetId}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData((d) => ({ ...d, avatar_url: res.avatar_url }));
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };
  const removeAvatar = async () => {
    setUploading(true);
    try {
      await api.delete(`/tenant/users/${targetId}/avatar`);
      setData((d) => ({ ...d, avatar_url: null }));
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Failed to remove"); }
    finally { setUploading(false); }
  };

  const onPickSignature = () => sigFileRef.current?.click();
  const uploadSignatureBlob = async (blob, filename = "signature.png") => {
    if (!blob) return;
    if (!/^image\//.test(blob.type || "image/png")) {
      setErr("Signature must be an image (PNG with transparent background works best).");
      return;
    }
    if (blob.size > 4 * 1024 * 1024) { setErr("Signature image too large (max 4 MB)."); return; }
    setSigUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      // Wrap the Blob in a File-like object so multipart upload preserves
      // a sensible filename in S3 / object storage.
      const file = blob instanceof File ? blob : new File([blob], filename, { type: blob.type || "image/png" });
      fd.append("file", file);
      const { data: res } = await api.post(`/tenant/users/${targetId}/signature`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData((d) => ({
        ...d,
        signature_url: res.signature_url,
        signature_updated_at: new Date().toISOString(),
      }));
      setShowPad(false);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Signature upload failed");
    } finally { setSigUploading(false); }
  };
  const onSignatureFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    await uploadSignatureBlob(f, f.name);
  };
  const removeSignature = async () => {
    setSigUploading(true);
    try {
      await api.delete(`/tenant/users/${targetId}/signature`);
      setData((d) => ({ ...d, signature_url: null, signature_updated_at: null }));
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Failed to remove signature"); }
    finally { setSigUploading(false); }
  };

  if (err) return (
    <div className="max-w-2xl vb-fade-in" data-testid="profile-error">
      <button onClick={() => navigate(-1)} className="vb-btn ghost mb-3"><ArrowLeft size={14} /> Back</button>
      <div className="vb-pill err">{err}</div>
    </div>
  );
  if (!data) return <div className="p-6 opacity-70" data-testid="profile-loading">Loading…</div>;

  const tabs = [
    { key: "profile", label: "Profile", icon: UserCircle2 },
    { key: "fsms", label: "FSMS", icon: ShieldCheck },
    ...(isModuleHidden("training-coms") ? [] : [{ key: "training", label: "Training", icon: GraduationCap }]),
    { key: "documents", label: "Documents", icon: FolderOpen },
    ...(canManageSignature ? [{ key: "signature", label: "Signature", icon: PenLine }] : []),
  ];

  return (
    <div className="max-w-3xl vb-fade-in" data-testid="member-profile">
      {!selfService && (
        <button onClick={() => navigate("/app/users")} className="vb-btn ghost mb-3" data-testid="profile-back"><ArrowLeft size={14} /> Back to team</button>
      )}

      <div className="vb-card p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="vb-avatar-wrap" data-testid="profile-avatar-wrap">
              {data.avatar_url ? (
                <img src={data.avatar_url} alt={data.name} className="vb-avatar" data-testid="profile-avatar-img" />
              ) : (
                <div
                  className="vb-avatar vb-avatar-fallback"
                  style={{ background: colorFor(data.id || data.name) }}
                  data-testid="profile-avatar-initials"
                >
                  {initialsOf(data.name)}
                </div>
              )}
              {canEditAvatar && (
                <button
                  type="button"
                  className="vb-avatar-edit"
                  onClick={onPickFile}
                  disabled={uploading}
                  title={data.avatar_url ? "Replace photo" : "Upload photo"}
                  data-testid="profile-avatar-upload"
                >
                  <Camera size={14} />
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={onFile}
                className="hidden"
                data-testid="profile-avatar-input"
              />
            </div>
            <div>
              <h1 className="vb-heading text-3xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }} data-testid="profile-name">
                {data.name}
                {data.is_archived && <span className="vb-pill ml-3 text-xs" style={{ background: "rgba(196, 84, 84, 0.15)", color: "var(--vb-error)" }}>Archived</span>}
                {isSelf && <span className="vb-pill ok ml-2 text-[10px]">you</span>}
              </h1>
              <div className="text-sm opacity-80">
                <span className="vb-pill mr-2">{data.role}</span>
                {data.company_role && <span className="opacity-70">{data.company_role}</span>}
              </div>
              {canEditAvatar && !!data.avatar_url && (
                <button
                  type="button"
                  className="vb-link text-xs mt-2 inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                  onClick={removeAvatar}
                  disabled={uploading}
                  data-testid="profile-avatar-remove"
                >
                  <Trash2 size={11} /> Remove photo
                </button>
              )}
            </div>
          </div>
          {isAdmin && !selfService && !data.is_archived && (
            <button className="vb-btn ghost" onClick={() => navigate("/app/users")} data-testid="profile-edit-link" title="Open the directory to edit / archive">
              <Archive size={14} /> Manage in directory
            </button>
          )}
        </div>
      </div>

      <div className="vb-tabs mb-4" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key} role="tab" aria-selected={tab === t.key}
            className={`vb-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
            data-testid={`profile-tab-${t.key}`}
          >
            <t.icon size={14} className="inline-block mr-1.5 -mt-0.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <div className="vb-card p-5" data-testid="profile-tab-content">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <Field icon={Mail} label="Email" value={data.email} />
            <Field icon={BadgeCheck} label="Employee ID" value={data.employee_id} />
            <Field icon={Calendar} label="Date joined" value={data.date_joined ? new Date(data.date_joined).toLocaleDateString() : null} />
            <Field icon={UserCircle2} label="Direct superior"
              value={superior
                ? `${superior.name} · ${superior.role}${superior.is_archived ? " (archived — needs reassignment)" : ""}`
                : null}
              warn={superior?.is_archived}
            />
            <Field icon={MapPin} label="Sites" value={siteNames.length ? siteNames.join(", ") : null} />
            <Field icon={Building2} label="Direct reports"
              value={reports.length ? reports.map((r) => r.name).join(", ") : null} />
            {data.is_archived && (
              <>
                <Field icon={Archive} label="Last day" value={data.last_day ? new Date(data.last_day).toLocaleDateString() : null} />
                <Field icon={Archive} label="Archive reason" value={data.archived_reason} />
              </>
            )}
          </dl>
        </div>
      )}

      {tab === "fsms" && (
        <div className="vb-card p-5" data-testid="profile-tab-fsms">
          <h2 className="vb-heading text-lg mb-3" style={{ color: "var(--vb-text-2)" }}>
            FSMS responsibilities
          </h2>
          {fsmsAssignments.length === 0 ? (
            <div className="text-center py-6 opacity-60 text-sm">
              <ShieldCheck size={32} className="mx-auto opacity-40 mb-2" />
              <p>No active FSMS role assignments.</p>
              <p className="text-xs mt-1">A manager can appoint roles via FSMS Team in the sidebar.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {fsmsAssignments.map((a) => (
                <div key={a.id} className="border-l-4 pl-4 py-2"
                  style={{ borderColor: "var(--vb-accent)" }}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-semibold" style={{ color: "var(--vb-text-2)" }}>
                        {a.role_label}
                      </div>
                      <div className="text-xs opacity-70">
                        at <strong>{a.site_name}</strong>
                        {a.job_title && <> · {a.job_title}</>}
                      </div>
                    </div>
                    <span className="text-[10px] opacity-50">
                      Since {(a.appointed_at || "").slice(0, 10)}
                    </span>
                  </div>
                  {a.qualifications && (
                    <div className="text-xs mt-2"><strong>Qualifications:</strong> {a.qualifications}</div>
                  )}
                  {a.responsibilities?.length > 0 && (
                    <ul className="list-disc pl-5 text-xs mt-2 space-y-0.5 opacity-80">
                      {a.responsibilities.map((r, i) => <li key={`${a.id}-r-${i}`}>{r}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "training" && (
        <TrainingTab targetId={targetId} canEdit={isAdmin} isArchived={!!data.is_archived} />
      )}

      {tab === "documents" && (
        <div className="vb-card p-8 text-center" data-testid="profile-tab-content">
          <FolderOpen size={36} className="mx-auto opacity-40 mb-3" />
          <p className="opacity-70 mb-1">Personal documents</p>
          <p className="text-xs opacity-50">Work permits, medical fitness certificates, signed SOPs.</p>
        </div>
      )}

      {tab === "signature" && canManageSignature && (
        <div className="vb-card p-5" data-testid="profile-tab-signature">
          <div className="flex items-start gap-3 mb-4">
            <PenLine size={22} style={{ color: "var(--vb-accent)" }} className="mt-0.5" />
            <div>
              <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>
                Your signature
              </h2>
              <p className="text-[12px] opacity-70 m-0 mt-0.5">
                Used when you sign off or approve generated reports. We capture
                the image bytes at the moment of signing, so historical reports
                stay unchanged if you ever replace it.
              </p>
            </div>
          </div>

          {!data.signature_url && (
            <div className="flex items-center gap-2 px-3 py-2 rounded mb-4 text-[12px]"
              data-testid="profile-signature-warning"
              style={{
                background: "rgba(245,158,11,0.10)",
                border: "1px solid rgba(245,158,11,0.45)",
              }}>
              <AlertTriangle size={14} style={{ color: "var(--vb-warning)" }} />
              <span>
                <strong>Signature required.</strong> Upload one below — you won&apos;t be
                able to sign off or approve audit-vault reports until this is set.
              </span>
            </div>
          )}

          <div className="vb-card p-4 mb-3"
            style={{ background: "var(--vb-bg-2,#faf6f1)", border: "1px dashed var(--vb-border)" }}>
            <div className="text-[10px] uppercase tracking-wider opacity-50 mb-2">Preview</div>
            <div className="flex items-end justify-center min-h-[120px] pb-2 border-b"
              style={{ borderColor: "var(--vb-border)" }} data-testid="profile-signature-preview-wrap">
              {data.signature_url ? (
                <img src={data.signature_url} alt="Your signature"
                  data-testid="profile-signature-preview"
                  style={{ maxHeight: 110, maxWidth: "90%", objectFit: "contain" }} />
              ) : (
                <div className="opacity-40 text-[12px] italic" data-testid="profile-signature-empty">
                  — no signature on file —
                </div>
              )}
            </div>
            <div className="text-[11px] opacity-60 text-center mt-2">
              {data.name} · {data.role}
              {data.signature_updated_at && (
                <> · Last updated {new Date(data.signature_updated_at).toLocaleDateString()}</>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button className="vb-btn sm" onClick={() => setShowPad((s) => !s)} disabled={sigUploading}
              data-testid="profile-signature-draw-toggle">
              <PenLine size={12} /> {showPad ? "Hide drawing pad" : (data.signature_url ? "Re-draw signature" : "Draw signature")}
            </button>
            <button className="vb-btn ghost sm" onClick={onPickSignature} disabled={sigUploading}
              data-testid="profile-signature-upload">
              <Upload size={12} /> {data.signature_url ? "Upload image" : "Upload image"}
            </button>
            {data.signature_url && (
              <button className="vb-btn sm ghost" onClick={removeSignature} disabled={sigUploading}
                data-testid="profile-signature-remove">
                <Trash2 size={12} /> Remove
              </button>
            )}
          </div>
          {showPad && (
            <div className="vb-card p-4 mt-3" data-testid="profile-signature-pad-wrap">
              <SignaturePad
                width={600}
                height={300}
                busy={sigUploading}
                onSave={(blob) => uploadSignatureBlob(blob, "drawn-signature.png")}
                onCancel={() => setShowPad(false)}
              />
            </div>
          )}
          <input ref={sigFileRef} type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={onSignatureFile} className="hidden"
            data-testid="profile-signature-input" />

          <div className="mt-4 text-[11px] opacity-70 space-y-1"
            style={{ lineHeight: 1.55 }}>
            <div><strong>Tips for a clean signature:</strong></div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>PNG with a transparent background looks crispest on the PDF.</li>
              <li>Sign on white paper, snap a phone photo, then remove the background (any free tool works).</li>
              <li>Keep it horizontal — landscape-oriented signatures fit the report sign-off box best.</li>
              <li>Max 4 MB. PNG, JPG, or WebP accepted.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value, warn }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider opacity-60 mb-1 flex items-center gap-1.5"><Icon size={12} /> {label}</dt>
      <dd className={`text-sm ${warn ? "" : ""}`} style={warn ? { color: "var(--vb-error)" } : undefined}>{value || <span className="opacity-40">—</span>}</dd>
    </div>
  );
}
